import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import ServerConfig from './ServerConfig';
import { getWebSocketService, WebSocketMessage } from '../services/WebSocketService';
import { amplifyConfig } from '../amplify-config';
import { useTheme } from '../design-system/ThemeProvider';
import { Button } from './UI/Button';
import { StatusBadge } from './UI/StatusBadge';
import { Modal } from './UI/Modal';

interface Schedule {
  id: string;
  action: 'start' | 'stop';
  hour?: number;
  minute?: number;
  days?: string[]; // Array of day codes: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
  cron?: string; // Backend format: "minute hour day month dayOfWeek"
  timezone: string;
  enabled?: boolean;
  description?: string;
}

interface Server {
  name: string;
  status: string;
  public_ip?: string;
  current_instance_id?: string;
  updated_at?: string;
  schedules?: Schedule[];
  cost_tracking?: {
    total_runtime_hours?: number;
    total_cost_usd?: number;
    cost_per_hour?: number;
  };
}

interface ServerListProps {
  user: any;
}

const ServerList: React.FC<ServerListProps> = ({ user }) => {
  const { theme } = useTheme();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    server_name: '',
    instance_type: 't3a.large',
    volume_size: 5
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ serverName: string; show: boolean }>({ serverName: '', show: false });
  const [showScheduleModal, setShowScheduleModal] = useState<{ serverName: string; show: boolean }>({ serverName: '', show: false });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [editingSchedules, setEditingSchedules] = useState<Schedule[]>([]);
  const [showConfigModal, setShowConfigModal] = useState<{ serverName: string; show: boolean }>({ serverName: '', show: false });
  const [wsConnected, setWsConnected] = useState(false);
  const [wsConnecting, setWsConnecting] = useState(false);

  const fetchServers = async () => {
    try {
      console.log('🔄 Starting fetchServers...');
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      console.log('🔑 Got ID token:', idToken ? 'YES' : 'NO');

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      console.log('📡 Making API request to servers endpoint...');
      const response = await fetch('https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 API response status:', response.status);
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Raw API response data:', data);
      console.log('📊 Servers array:', data.servers);
      console.log('📊 Number of servers:', data.servers ? data.servers.length : 0);
      
      if (data.servers && data.servers.length > 0) {
        data.servers.forEach((server: any, index: number) => {
          console.log(`🖥️ Server ${index + 1}:`, {
            name: server.name,
            status: server.status,
            statusType: typeof server.status,
            statusLowerCase: server.status?.toLowerCase(),
            public_ip: server.public_ip,
            current_instance_id: server.current_instance_id
          });
        });
      }

      setServers(data.servers || []);
      setError(null);
      console.log('✅ fetchServers completed successfully');
    } catch (err) {
      console.error('❌ Error fetching servers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch servers');
    } finally {
      setLoading(false);
    }
  };

  // WebSocket event handlers
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    console.log('📨 Processing WebSocket message:', message);
    
    if (message.type === 'server_update' && message.server_name && message.server_data) {
      setServers(prevServers => {
        const serverIndex = prevServers.findIndex(s => s.name === message.server_name);
        
        if (message.server_data?.deleted) {
          // Server was deleted
          console.log(`🗑️ Server ${message.server_name} was deleted, removing from list`);
          return prevServers.filter(s => s.name !== message.server_name);
        }
        
        if (serverIndex !== -1) {
          // Update existing server
          const updatedServers = [...prevServers];
          updatedServers[serverIndex] = {
            ...updatedServers[serverIndex],
            ...message.server_data,
          };
          console.log(`🔄 Updated server ${message.server_name}:`, updatedServers[serverIndex]);
          return updatedServers;
        } else {
          // Server not found in list, might be new - refresh list
          console.log(`➕ Server ${message.server_name} not found in list, refreshing...`);
          fetchServers();
          return prevServers;
        }
      });
    }
  }, []);

  const handleWebSocketConnection = useCallback((message: WebSocketMessage) => {
    console.log('🔌 WebSocket connected');
    setWsConnected(true);
    setWsConnecting(false);
  }, []);

  const handleWebSocketDisconnect = useCallback((message: WebSocketMessage) => {
    console.log('🔌 WebSocket disconnected');
    setWsConnected(false);
    setWsConnecting(false);
  }, []);

  const handleWebSocketError = useCallback((message: WebSocketMessage) => {
    console.log('❌ WebSocket error');
    setWsConnected(false);
    setWsConnecting(false);
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    if (user) {
      console.log('🔌 Initializing WebSocket connection...');
      setWsConnecting(true);
      
      try {
        const wsService = getWebSocketService(amplifyConfig.WebSocket.url);
        
        // Set up event listeners
        wsService.on('server_update', handleWebSocketMessage);
        wsService.on('connection', handleWebSocketConnection);
        wsService.on('disconnect', handleWebSocketDisconnect);
        wsService.on('error', handleWebSocketError);
        
        // Connect to WebSocket
        wsService.connect().catch(error => {
          console.error('❌ Failed to connect to WebSocket:', error);
          setWsConnecting(false);
        });
        
        // Cleanup function
        return () => {
          console.log('🧹 Cleaning up WebSocket listeners...');
          wsService.off('server_update', handleWebSocketMessage);
          wsService.off('connection', handleWebSocketConnection);
          wsService.off('disconnect', handleWebSocketDisconnect);
          wsService.off('error', handleWebSocketError);
        };
      } catch (error) {
        console.error('❌ Error initializing WebSocket service:', error);
        setWsConnecting(false);
      }
    }
  }, [user, handleWebSocketMessage, handleWebSocketConnection, handleWebSocketDisconnect, handleWebSocketError]);

  useEffect(() => {
    if (user) {
      fetchServers();
    }
  }, [user]);

  const performServerAction = async (serverName: string, action: 'start' | 'stop') => {
    try {
      console.log(`🚀 Performing ${action} action on server: ${serverName}`);
      setActionLoading(prev => ({ ...prev, [serverName]: true }));
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      console.log(`🔑 Got ID token for ${action}:`, idToken ? 'YES' : 'NO');

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const apiUrl = `https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers/${action}`;
      const payload = { server_name: serverName };
      console.log(`📡 Making ${action} request to:`, apiUrl);
      console.log(`📡 Request payload:`, payload);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log(`📡 ${action} response status:`, response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.log(`❌ ${action} response error:`, errorText);
        throw new Error(`${action} request failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`✅ ${action} result:`, result);
      
      // Real-time updates will handle the refresh via WebSocket
      
    } catch (err) {
      console.error(`❌ Error ${action}ing server:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action} server`);
    } finally {
      setActionLoading(prev => ({ ...prev, [serverName]: false }));
    }
  };

  const startServer = (serverName: string) => performServerAction(serverName, 'start');
  const stopServer = (serverName: string) => performServerAction(serverName, 'stop');

  const createServer = async () => {
    try {
      console.log('🏗️ Creating new server:', createFormData);
      setCreateLoading(true);
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      console.log('🔑 Got ID token for create:', idToken ? 'YES' : 'NO');

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const apiUrl = 'https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers';
      console.log('📡 Making create request to:', apiUrl);
      console.log('📡 Request payload:', createFormData);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createFormData),
      });

      console.log('📡 Create response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Create response error:', errorText);
        let errorMessage = `Create request failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Use default error message if can't parse JSON
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Create result:', result);
      
      // Reset form and close modal
      setCreateFormData({
        server_name: '',
        instance_type: 't3a.large',
        volume_size: 5
      });
      setShowCreateForm(false);
      
      // Real-time updates will handle the refresh via WebSocket
      console.log('✅ Server creation initiated, waiting for WebSocket updates...');
      
    } catch (err) {
      console.error('❌ Error creating server:', err);
      setError(err instanceof Error ? err.message : 'Failed to create server');
    } finally {
      setCreateLoading(false);
    }
  };

  const deleteServer = async (serverName: string) => {
    try {
      console.log('🗑️ Deleting server:', serverName);
      setActionLoading(prev => ({ ...prev, [serverName]: true }));
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      console.log('🔑 Got ID token for delete:', idToken ? 'YES' : 'NO');

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      const apiUrl = `https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers/${encodeURIComponent(serverName)}`;
      console.log('📡 Making delete request to:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Delete response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Delete response error:', errorText);
        let errorMessage = `Delete request failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Use default error message if can't parse JSON
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Delete result:', result);
      
      // Close confirmation dialog
      setDeleteConfirm({ serverName: '', show: false });
      
      // Real-time updates will handle the refresh via WebSocket
      console.log('✅ Server deletion initiated, waiting for WebSocket updates...');
      
    } catch (err) {
      console.error('❌ Error deleting server:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete server');
      setDeleteConfirm({ serverName: '', show: false });
    } finally {
      setActionLoading(prev => ({ ...prev, [serverName]: false }));
    }
  };

  const openScheduleModal = (serverName: string) => {
    const server = servers.find(s => s.name === serverName);
    const currentSchedules = server?.schedules || [];
    
    // Convert backend schedules (with cron) to frontend format (with hour/minute/days)
    const convertedSchedules = currentSchedules.map(schedule => {
      if (schedule.cron && (!schedule.hour && !schedule.minute && !schedule.days)) {
        const parsed = parseCron(schedule.cron);
        return {
          ...schedule,
          hour: parsed.hour,
          minute: parsed.minute,
          days: parsed.days
        };
      }
      return schedule;
    });
    
    setEditingSchedules([...convertedSchedules]);
    setShowScheduleModal({ serverName, show: true });
  };

  const addNewSchedule = () => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const newSchedule: Schedule = {
      id: `schedule-${Date.now()}`,
      action: 'start',
      hour: 20,
      minute: 0,
      days: ['SAT', 'SUN'],
      timezone: userTimezone,
      enabled: true,
      description: ''
    };
    setEditingSchedules(prev => [...prev, newSchedule]);
  };

  const updateSchedule = (index: number, field: keyof Schedule, value: any) => {
    setEditingSchedules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeSchedule = (index: number) => {
    setEditingSchedules(prev => prev.filter((_, i) => i !== index));
  };

  const saveSchedules = async () => {
    try {
      console.log('💾 Saving schedules for:', showScheduleModal.serverName);
      setScheduleLoading(true);
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      // Convert UI schedules to backend format with cron
      const backendSchedules = editingSchedules.map(schedule => ({
        id: schedule.id,
        action: schedule.action,
        cron: scheduleToCron(schedule),
        timezone: schedule.timezone,
        enabled: schedule.enabled,
        description: schedule.description
      }));

      const apiUrl = `https://inc17t38tg.execute-api.us-east-1.amazonaws.com/prod/servers/${encodeURIComponent(showScheduleModal.serverName)}/schedules`;
      console.log('📡 Making schedule update request to:', apiUrl);
      console.log('📡 Schedule payload:', { schedules: backendSchedules });

      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schedules: backendSchedules }),
      });

      console.log('📡 Schedule update response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Schedule update response error:', errorText);
        let errorMessage = `Schedule update failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Use default error message if can't parse JSON
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Schedule update result:', result);
      
      // Close modal
      setShowScheduleModal({ serverName: '', show: false });
      
      // Real-time updates will handle the refresh via WebSocket
      console.log('✅ Schedule update initiated, waiting for WebSocket updates...');
      
    } catch (err) {
      console.error('❌ Error updating schedules:', err);
      setError(err instanceof Error ? err.message : 'Failed to update schedules');
    } finally {
      setScheduleLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Parse cron expression back to schedule components
  const parseCron = (cron: string): { hour: number; minute: number; days: string[] } => {
    try {
      // Remove extra * at the end if present, and split
      const cleanCron = cron.replace(/ \*$/, '');
      const parts = cleanCron.split(' ');
      
      if (parts.length >= 5) {
        const minute = parseInt(parts[0]) || 0;
        const hour = parseInt(parts[1]) || 0;
        const dayOfWeek = parts[4];
        
        // Parse days - handle comma-separated values
        let days: string[] = [];
        if (dayOfWeek !== '*') {
          days = dayOfWeek.split(',').filter(d => d.length > 0);
        }
        
        return { hour, minute, days };
      }
    } catch (error) {
      console.warn('Failed to parse cron expression:', cron, error);
    }
    
    return { hour: 0, minute: 0, days: [] };
  };

  // Convert user-friendly schedule to EventBridge Scheduler cron format (6 fields)
  const scheduleToCron = (schedule: Schedule): string => {
    const hour = schedule.hour ?? 0;
    const minute = schedule.minute ?? 0;
    const days = schedule.days || [];
    const daysStr = days.join(',');
    // EventBridge Scheduler requires 6-field cron: minute hour day-of-month month day-of-week year
    // Use "?" for day-of-month when specifying day-of-week (mutually exclusive)
    return `${minute} ${hour} ? * ${daysStr} *`;
  };

  // Format schedule for display
  const formatSchedule = (schedule: Schedule): string => {
    let hour, minute, days;
    
    // If schedule has cron, parse it; otherwise use direct properties
    if (schedule.cron) {
      const parsed = parseCron(schedule.cron);
      hour = parsed.hour;
      minute = parsed.minute;
      days = parsed.days;
    } else {
      hour = schedule.hour ?? 0;
      minute = schedule.minute ?? 0;
      days = schedule.days || [];
    }
    
    // Convert to 12-hour format
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'AM' : 'PM';
    const timeStr = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
    
    const daysStr = days.length === 7 ? 'Daily' : 
                   days.length === 5 && !days.includes('SAT') && !days.includes('SUN') ? 'Weekdays' :
                   days.length === 2 && days.includes('SAT') && days.includes('SUN') ? 'Weekends' :
                   days.length === 0 ? 'Daily' :
                   days.join(', ');
    return `${daysStr} at ${timeStr} (${schedule.timezone || 'UTC'})`;
  };


  // Get quick preset schedules
  const getQuickPresets = () => [
    {
      name: 'Weekend Evenings',
      schedule: { action: 'start' as const, hour: 19, minute: 0, days: ['SAT', 'SUN'] }
    },
    {
      name: 'Weekday Evenings', 
      schedule: { action: 'start' as const, hour: 18, minute: 0, days: ['MON', 'TUE', 'WED', 'THU', 'FRI'] }
    },
    {
      name: 'Daily Morning',
      schedule: { action: 'start' as const, hour: 8, minute: 0, days: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] }
    },
    {
      name: 'Auto Stop Late Night',
      schedule: { action: 'stop' as const, hour: 23, minute: 0, days: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] }
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return '#4CAF50';
      case 'stopped':
        return '#757575';
      case 'starting':
        return '#FF9800';
      case 'stopping':
        return '#FF5722';
      default:
        return '#757575';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading servers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <p>Error: {error}</p>
        <button onClick={fetchServers} style={{ marginTop: '10px', padding: '8px 16px' }}>
          Retry
        </button>
      </div>
    );
  }

  // Calculate overall metrics
  const totalServers = servers.length;
  const runningServers = servers.filter(s => s.status?.toLowerCase() === 'running').length;
  const totalCost = servers.reduce((acc, server) => acc + (server.cost_tracking?.total_cost_usd || 0), 0);
  const totalRuntime = servers.reduce((acc, server) => acc + (server.cost_tracking?.total_runtime_hours || 0), 0);

  return (
    <div style={{ 
      padding: theme.componentSpacing.dashboard.headerPadding,
      minHeight: '100vh',
      backgroundColor: theme.semanticColors.background.primary
    }}>
      {/* Page Header */}
      <div style={{ marginBottom: theme.componentSpacing.dashboard.sectionGap }}>
        <h1 style={{
          margin: 0,
          ...theme.typographyStyles.h1,
          color: theme.semanticColors.text.primary
        }}>
          Server Overview
        </h1>
        <p style={{
          margin: `${theme.spacing[2]} 0 0 0`,
          ...theme.typographyStyles.bodyLarge,
          color: theme.semanticColors.text.secondary
        }}>
          Monitor your Minecraft servers and track performance metrics
        </p>
      </div>

      {/* Metrics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.componentSpacing.dashboard.widgetGap,
        marginBottom: theme.componentSpacing.dashboard.sectionGap
      }}>
        {/* Total Servers */}
        <div style={{
          ...theme.componentTokens.metricCard,
          backgroundColor: theme.componentTokens.metricCard.background,
          padding: theme.componentTokens.metricCard.padding,
          borderRadius: theme.componentTokens.metricCard.borderRadius,
          border: `1px solid ${theme.componentTokens.metricCard.border}`,
          boxShadow: theme.componentTokens.metricCard.shadow
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing[4]
          }}>
            <span style={{
              ...theme.componentTokens.metricCard.header,
              fontSize: theme.componentTokens.metricCard.header.fontSize,
              fontWeight: theme.componentTokens.metricCard.header.fontWeight,
              color: theme.componentTokens.metricCard.header.text
            }}>
              Total Servers
            </span>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: theme.borderRadius.xl,
              backgroundColor: theme.colors.primary[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🖥️
            </div>
          </div>
          <div style={{
            ...theme.componentTokens.metricCard.value,
            fontSize: theme.componentTokens.metricCard.value.fontSize,
            fontWeight: theme.componentTokens.metricCard.value.fontWeight,
            color: theme.componentTokens.metricCard.value.text,
            lineHeight: theme.componentTokens.metricCard.value.lineHeight
          }}>
            {totalServers}
          </div>
        </div>

        {/* Running Servers */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280'
            }}>
              Running Now
            </span>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ⚡
            </div>
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#111827',
            lineHeight: 1
          }}>
            {runningServers}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#16a34a',
            fontWeight: '500',
            marginTop: '4px'
          }}>
            {totalServers > 0 ? Math.round((runningServers / totalServers) * 100) : 0}% active
          </div>
        </div>

        {/* Total Cost */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280'
            }}>
              Total Cost
            </span>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              💰
            </div>
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#111827',
            lineHeight: 1
          }}>
            {formatCurrency(totalCost)}
          </div>
        </div>

        {/* Total Runtime */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280'
            }}>
              Runtime Hours
            </span>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#e0e7ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ⏱️
            </div>
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#111827',
            lineHeight: 1
          }}>
            {Math.round(totalRuntime)}h
          </div>
        </div>
      </div>

      {/* Servers Section Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.componentSpacing.dashboard.widgetGap }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[4] }}>
          <h2 style={{ 
            margin: 0, 
            ...theme.typographyStyles.h2,
            color: theme.semanticColors.text.primary
          }}>
            Your Servers
          </h2>
          {/* WebSocket Connection Status */}
          <StatusBadge 
            status={wsConnected ? 'running' : wsConnecting ? 'starting' : 'error'}
            size="md"
          >
            {wsConnected ? 'Live' : wsConnecting ? 'Connecting' : 'Offline'}
          </StatusBadge>
        </div>
        <div style={{ display: 'flex', gap: theme.spacing[3] }}>
          <Button onClick={() => setShowCreateForm(true)}>
            + Create Server
          </Button>
          {!wsConnected && !wsConnecting && (
            <Button 
              variant="secondary"
              onClick={() => {
                try {
                  const wsService = getWebSocketService();
                  wsService.reconnect();
                  setWsConnecting(true);
                } catch (error) {
                  console.error('❌ Error reconnecting WebSocket:', error);
                }
              }}
            >
              🔄 Reconnect
            </Button>
          )}
        </div>
      </div>

      {/* Create Server Form */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create New Server"
        size="md"
      >
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Server Name *
              </label>
              <input
                type="text"
                value={createFormData.server_name}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, server_name: e.target.value }))}
                placeholder="e.g. my-survival-world"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Only letters, numbers, and hyphens allowed
              </small>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Instance Type
              </label>
              <select
                value={createFormData.instance_type}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, instance_type: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="t3a.large">t3a.large (2 vCPU, 8 GB RAM) - ~$0.0752/hour</option>
                <option value="t3a.xlarge">t3a.xlarge (4 vCPU, 16 GB RAM) - ~$0.1504/hour</option>
                <option value="t3a.2xlarge">t3a.2xlarge (8 vCPU, 32 GB RAM) - ~$0.3008/hour</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                Storage Size (GB)
              </label>
              <input
                type="number"
                min="5"
                max="100"
                value={createFormData.volume_size}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, volume_size: parseInt(e.target.value) || 5 }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Persistent storage for your Minecraft world (5-100 GB)
              </small>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                onClick={() => setShowCreateForm(false)}
                disabled={createLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={createServer}
                disabled={createLoading || !createFormData.server_name.trim()}
                loading={createLoading}
              >
                Create Server
              </Button>
            </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ serverName: '', show: false })}
        title="⚠️ Delete Server"
        size="sm"
      >
            
            <p style={{ margin: '0 0 20px 0', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong>{deleteConfirm.serverName}</strong>?
            </p>
            
            <div style={{ 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: '4px', 
              padding: '12px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>⚠️ Warning:</strong> This action cannot be undone. All world data and the EBS volume will be permanently deleted.
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                onClick={() => setDeleteConfirm({ serverName: '', show: false })}
                disabled={actionLoading[deleteConfirm.serverName]}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteServer(deleteConfirm.serverName)}
                disabled={actionLoading[deleteConfirm.serverName]}
                loading={actionLoading[deleteConfirm.serverName]}
              >
                Delete Server
              </Button>
            </div>
      </Modal>

      {/* Schedule Management Modal */}
      {showScheduleModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Manage Schedules for {showScheduleModal.serverName}</h3>
            
            <div style={{ marginBottom: '20px' }}>
              {/* Quick Presets */}
              {editingSchedules.length === 0 && (
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '6px', border: '1px solid #e3f2fd' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1976d2' }}>Quick Start Templates</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                    {getQuickPresets().map((preset, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                          const newSchedule: Schedule = {
                            id: `schedule-${Date.now()}`,
                            ...preset.schedule,
                            timezone: userTimezone,
                            enabled: true,
                            description: preset.name
                          };
                          setEditingSchedules([newSchedule]);
                        }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          border: '1px solid #2196F3',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          color: '#1976d2',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {editingSchedules.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No schedules configured. Use templates above or add a custom schedule.</p>
              ) : (
                editingSchedules.map((schedule, index) => (
                  <div key={index} style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '8px', 
                    padding: '16px', 
                    marginBottom: '12px',
                    backgroundColor: schedule.enabled !== false ? '#f9f9f9' : '#f5f5f5',
                    opacity: schedule.enabled !== false ? 1 : 0.7
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                          Action
                        </label>
                        <select
                          value={schedule.action}
                          onChange={(e) => updateSchedule(index, 'action', e.target.value as 'start' | 'stop')}
                          style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="start">Start Server</option>
                          <option value="stop">Stop Server</option>
                        </select>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                          Time
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select
                            value={schedule.hour ?? 0}
                            onChange={(e) => updateSchedule(index, 'hour', parseInt(e.target.value))}
                            style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '60px' }}
                          >
                            {Array.from({ length: 24 }, (_, i) => {
                              const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
                              const ampm = i < 12 ? 'AM' : 'PM';
                              return (
                                <option key={i} value={i}>
                                  {i.toString().padStart(2, '0')} ({hour12} {ampm})
                                </option>
                              );
                            })}
                          </select>
                          <span>:</span>
                          <select
                            value={schedule.minute ?? 0}
                            onChange={(e) => updateSchedule(index, 'minute', parseInt(e.target.value))}
                            style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '60px' }}
                          >
                            {Array.from({ length: 12 }, (_, i) => i * 5).map(minute => (
                              <option key={minute} value={minute}>
                                {minute.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {(schedule.hour ?? 0).toString().padStart(2, '0')}:{(schedule.minute ?? 0).toString().padStart(2, '0')} UTC
                        </div>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                          Days
                        </label>
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => updateSchedule(index, 'days', ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])}
                              style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                border: '1px solid #ddd',
                                borderRadius: '12px',
                                backgroundColor: (schedule.days || []).length === 7 ? '#e3f2fd' : 'white',
                                cursor: 'pointer'
                              }}
                            >
                              All
                            </button>
                            <button
                              type="button"
                              onClick={() => updateSchedule(index, 'days', ['MON', 'TUE', 'WED', 'THU', 'FRI'])}
                              style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                border: '1px solid #ddd',
                                borderRadius: '12px',
                                backgroundColor: 
                                  (schedule.days || []).length === 5 && 
                                  ['MON', 'TUE', 'WED', 'THU', 'FRI'].every(d => (schedule.days || []).includes(d)) &&
                                  !(schedule.days || []).includes('SAT') && !(schedule.days || []).includes('SUN')
                                    ? '#e3f2fd' : 'white',
                                cursor: 'pointer'
                              }}
                            >
                              Weekdays
                            </button>
                            <button
                              type="button"
                              onClick={() => updateSchedule(index, 'days', ['SAT', 'SUN'])}
                              style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                border: '1px solid #ddd',
                                borderRadius: '12px',
                                backgroundColor: 
                                  (schedule.days || []).length === 2 && 
                                  (schedule.days || []).includes('SAT') && (schedule.days || []).includes('SUN')
                                    ? '#e3f2fd' : 'white',
                                cursor: 'pointer'
                              }}
                            >
                              Weekends
                            </button>
                            <button
                              type="button"
                              onClick={() => updateSchedule(index, 'days', [])}
                              style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                border: '1px solid #ddd',
                                borderRadius: '12px',
                                backgroundColor: 'white',
                                cursor: 'pointer'
                              }}
                            >
                              Clear
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                            {[
                              { code: 'MON', label: 'Mon' },
                              { code: 'TUE', label: 'Tue' },
                              { code: 'WED', label: 'Wed' },
                              { code: 'THU', label: 'Thu' },
                              { code: 'FRI', label: 'Fri' },
                              { code: 'SAT', label: 'Sat' },
                              { code: 'SUN', label: 'Sun' }
                            ].map(({ code, label }) => (
                              <button
                                key={code}
                                type="button"
                                onClick={() => {
                                  const currentDays = schedule.days || [];
                                  const newDays = currentDays.includes(code)
                                    ? currentDays.filter(d => d !== code)
                                    : [...currentDays, code];
                                  updateSchedule(index, 'days', newDays);
                                }}
                                style={{
                                  padding: '8px 4px',
                                  fontSize: '12px',
                                  border: '2px solid',
                                  borderColor: (schedule.days || []).includes(code) ? '#2196F3' : '#ddd',
                                  borderRadius: '6px',
                                  backgroundColor: (schedule.days || []).includes(code) ? '#e3f2fd' : 'white',
                                  color: (schedule.days || []).includes(code) ? '#1976d2' : '#666',
                                  cursor: 'pointer',
                                  fontWeight: (schedule.days || []).includes(code) ? 'bold' : 'normal',
                                  transition: 'all 0.2s'
                                }}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                          Timezone
                        </label>
                        <select
                          value={schedule.timezone}
                          onChange={(e) => updateSchedule(index, 'timezone', e.target.value)}
                          style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="UTC">UTC</option>
                          <option value="Europe/London">London (GMT)</option>
                          <option value="Europe/Paris">Paris (CET)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                        </select>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                          <input
                            type="checkbox"
                            checked={schedule.enabled !== false}
                            onChange={(e) => updateSchedule(index, 'enabled', e.target.checked)}
                            style={{ marginRight: '6px' }}
                          />
                          Enabled
                        </label>
                        <button
                          onClick={() => removeSchedule(index)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        value={schedule.description || ''}
                        onChange={(e) => updateSchedule(index, 'description', e.target.value)}
                        placeholder="Weekend gaming hours"
                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>
                    
                    {/* Schedule Preview */}
                    <div style={{ gridColumn: '1 / -1', marginTop: '8px', padding: '8px', backgroundColor: '#e8f5e8', borderRadius: '4px', fontSize: '13px' }}>
                      <strong>Preview:</strong> This will {schedule.action} the server {formatSchedule(schedule)}
                      <br />
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        Cron: {scheduleToCron(schedule)}
                      </span>
                    </div>
                  </div>
                ))
              )}
              
              <button
                onClick={addNewSchedule}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Add New Schedule
              </button>
            </div>

            <div style={{ 
              borderTop: '1px solid #ddd', 
              paddingTop: '16px',
              backgroundColor: '#f5f5f5',
              margin: '0 -24px -24px -24px',
              padding: '16px 24px',
              borderRadius: '0 0 8px 8px'
            }}>
              <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                <strong>Common Cron Patterns:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  <li><code>0 20 * * 6,0</code> - Weekends at 8:00 PM UTC</li>
                  <li><code>0 22 * * 6,0</code> - Weekends at 10:00 PM UTC</li>
                  <li><code>0 18 * * 1-5</code> - Weekdays at 6:00 PM UTC</li>
                  <li><code>30 19 * * *</code> - Daily at 7:30 PM UTC</li>
                </ul>
              </div>
              
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowScheduleModal({ serverName: '', show: false })}
                  disabled={scheduleLoading}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveSchedules}
                  disabled={scheduleLoading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: scheduleLoading ? 'not-allowed' : 'pointer',
                    opacity: scheduleLoading ? 0.6 : 1,
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {scheduleLoading ? 'Saving...' : 'Save Schedules'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {servers.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: `${theme.spacing[16]} ${theme.spacing[6]}`,
          backgroundColor: theme.semanticColors.surface.primary,
          borderRadius: theme.borderRadius.xl,
          border: `1px solid ${theme.semanticColors.border.primary}`,
          boxShadow: theme.componentTokens.card.shadow
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: theme.semanticColors.surface.tertiary,
            borderRadius: theme.borderRadius.xl,
            margin: `0 auto ${theme.spacing[6]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            🖥️
          </div>
          <h3 style={{
            ...theme.typographyStyles.h3,
            color: theme.semanticColors.text.primary,
            margin: `0 0 ${theme.spacing[2]} 0`
          }}>
            No servers yet
          </h3>
          <p style={{ 
            ...theme.typographyStyles.body,
            color: theme.semanticColors.text.secondary,
            margin: 0
          }}>
            Create your first Minecraft server to get started with monitoring and analytics.
          </p>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '24px'
        }}>
          {servers.map((server) => {
            console.log(`🎮 Rendering server: ${server.name}`);
            console.log(`📊 Server status: "${server.status}" (type: ${typeof server.status})`);
            console.log(`📊 Status toLowerCase: "${server.status?.toLowerCase()}"`);
            
            const showStartButton = server.status?.toLowerCase() === 'stopped';
            const showStopButton = server.status?.toLowerCase() === 'running' || server.status?.toLowerCase() === 'starting';
            const showStoppingMessage = server.status?.toLowerCase() === 'stopping';
            
            console.log(`🔘 Button visibility for ${server.name}:`, {
              showStartButton,
              showStopButton, 
              showStoppingMessage,
              rawStatus: server.status
            });
            
            return (
            <div
              key={server.name}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: '600',
                    color: '#111827',
                    letterSpacing: '-0.025em'
                  }}>
                    {server.name}
                  </h3>
                  <p style={{ 
                    margin: '2px 0 0 0', 
                    fontSize: '13px', 
                    color: '#6b7280',
                    fontWeight: '400'
                  }}>
                    Minecraft Server
                  </p>
                </div>
                <StatusBadge 
                  status={server.status?.toLowerCase() === 'running' ? 'running' :
                          server.status?.toLowerCase() === 'starting' ? 'starting' :
                          server.status?.toLowerCase() === 'stopping' ? 'error' :
                          'stopped'}
                />
              </div>

              {/* Key Information - Minimal Display */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                gap: '12px', 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #f3f4f6'
              }}>
                {server.status?.toLowerCase() === 'running' && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#6b7280',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Address
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: '#166534',
                      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
                    }}>
                      {server.name}.mcfa.cloud
                    </div>
                  </div>
                )}
                
                {server.cost_tracking?.total_cost_usd !== undefined && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#6b7280',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Total Cost
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: '#111827'
                    }}>
                      {formatCurrency(server.cost_tracking.total_cost_usd)}
                    </div>
                  </div>
                )}
                
                {server.cost_tracking?.total_runtime_hours !== undefined && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#6b7280',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Runtime
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: '#111827'
                    }}>
                      {Math.round(server.cost_tracking.total_runtime_hours)}h
                    </div>
                  </div>
                )}
                
                {server.schedules && server.schedules.length > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: '500', 
                      color: '#6b7280',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Schedules
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: '#7c3aed'
                    }}>
                      {server.schedules.length} active
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {(() => {
                console.log(`🔘 Evaluating START button for ${server.name}: showStartButton=${showStartButton}`);
                console.log(`🔘 Evaluating STOP button for ${server.name}: showStopButton=${showStopButton}`);
                console.log(`🔘 Evaluating STOPPING message for ${server.name}: showStoppingMessage=${showStoppingMessage}`);
                return null;
              })()}
              <div style={{ 
                marginTop: '16px', 
                display: 'flex', 
                gap: '8px', 
                flexWrap: 'wrap',
                borderTop: '1px solid #f3f4f6',
                paddingTop: '16px'
              }}>
                {showStartButton && (
                  <Button
                    onClick={() => startServer(server.name)}
                    disabled={actionLoading[server.name]}
                    loading={actionLoading[server.name]}
                    size="sm"
                  >
                    Start
                  </Button>
                )}
                
                {showStopButton && (
                  <Button
                    variant="danger"
                    onClick={() => stopServer(server.name)}
                    disabled={actionLoading[server.name]}
                    loading={actionLoading[server.name]}
                    size="sm"
                  >
                    Stop
                  </Button>
                )}
                
                {showStoppingMessage && (
                  <div style={{ 
                    padding: '12px 24px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    color: '#ef4444',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#ef4444',
                      boxShadow: '0 0 12px #ef4444',
                      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                    }} />
                    Server is stopping...
                  </div>
                )}
                
                {/* Manage Schedules button */}
                <Button
                  variant="secondary"
                  onClick={() => openScheduleModal(server.name)}
                  disabled={actionLoading[server.name]}
                  size="sm"
                >
                  Schedules
                </Button>

                {/* Configure Server button */}
                <Button
                  variant="secondary"
                  onClick={() => setShowConfigModal({ serverName: server.name, show: true })}
                  disabled={actionLoading[server.name]}
                  size="sm"
                >
                  Configure
                </Button>

                {/* Delete button - only show if server is stopped */}
                {server.status?.toLowerCase() === 'stopped' && (
                  <Button
                    variant="danger"
                    onClick={() => setDeleteConfirm({ serverName: server.name, show: true })}
                    disabled={actionLoading[server.name]}
                    size="sm"
                  >
                    🗑️ Delete
                  </Button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Server Configuration Modal */}
      {showConfigModal.show && (
        <ServerConfig
          serverName={showConfigModal.serverName}
          onClose={() => setShowConfigModal({ serverName: '', show: false })}
          onSave={() => {
            setShowConfigModal({ serverName: '', show: false });
            fetchServers(); // Refresh server list to show any updated information
          }}
        />
      )}
    </div>
  );
};

export default ServerList;